// 수학 출제하기의 '내 PDF 출제' 탭. 선생님이 수학 사이트에 올린 문서(문항관리 · 변형문제 · 오답노트 · 수포자)를
// 폴더 · 문서 그대로 보고, 문서를 열어 문항을 체크해 담거나 고른 문서에서 조건으로 뽑는다(수학 사이트 6절).
import React, { useEffect, useMemo, useRef, useState } from "react";

import apiClient from "../../../services/apiClient";
import {
  MathBankChipList,
  MathBankFilterFields,
  MathBankProblemCard,
  toggleInList,
} from "./MathBankControls";
import { MAX_TOTAL, problemFilterPayloadOf } from "./mathBankShared";

const EMPTY_DOC_PAGE = { page: 1, pages: 1, problem_list: [] };

const MathBankDocPanel = ({ isActive, filter, onFilterChange, pickedProblemIdList, onAddProblems, onPicked }) => {
  const [docData, setDocData] = useState(null);
  const [docSectionKey, setDocSectionKey] = useState("");
  const [openFolderKeyList, setOpenFolderKeyList] = useState([]);
  const [docIdList, setDocIdList] = useState([]);
  const [viewingDoc, setViewingDoc] = useState(null);
  const [docProblemPage, setDocProblemPage] = useState(EMPTY_DOC_PAGE);
  const [isLoadingProblems, setIsLoadingProblems] = useState(false);
  const [checkedProblemIdList, setCheckedProblemIdList] = useState([]);
  const [total, setTotal] = useState(20);
  const [isPicking, setIsPicking] = useState(false);
  const problemPaneRef = useRef(null);

  // 폴더는 이 탭을 처음 열 때 받는다
  useEffect(() => {
    if (!isActive || docData) return;
    apiClient
      .get("/api/v1/assignment/math/bank/docs", { withAuth: true })
      .then((response) => {
        setDocData(response.data);
        const firstSection = response.data.sections?.[0];
        setDocSectionKey(firstSection?.section || "");
        // 첫 폴더는 펼쳐 둔다
        if (firstSection?.folders?.[0]) {
          setOpenFolderKeyList([`${firstSection.section}:${firstSection.folders[0].id ?? "none"}`]);
        }
      })
      .catch((error) => setDocData({ sections: [], error: error?.response?.data?.error || "내 PDF 폴더를 불러오지 못했습니다." }));
  }, [isActive, docData]);

  const docSectionList = useMemo(() => docData?.sections || [], [docData]);
  const docSection = docSectionList.find((one) => one.section === docSectionKey);
  const docTitleById = useMemo(() => {
    const titleById = {};
    docSectionList.forEach((section) =>
      section.folders.forEach((folder) => folder.docs.forEach((doc) => (titleById[doc.id] = doc.title)))
    );
    return titleById;
  }, [docSectionList]);

  const filterQuery = useMemo(() => {
    const payload = problemFilterPayloadOf(filter);
    const query = new URLSearchParams({ source: payload.source, dbs: payload.dbs.join(",") });
    if (payload.has_twin) query.set("has_twin", "1");
    if (payload.has_handwritten_explain) query.set("has_handwritten_explain", "1");
    return query.toString();
  }, [filter]);

  // 늦게 온 응답이 나중에 연 문서를 덮지 않게, 마지막으로 부른 것만 받는다
  const requestSequenceRef = useRef(0);
  const loadDocProblems = async (doc, page = 1) => {
    const sequence = ++requestSequenceRef.current;
    setIsLoadingProblems(true);
    try {
      const response = await apiClient.get(
        `/api/v1/assignment/math/bank/doc-problems?doc_id=${doc.id}&page=${page}&${filterQuery}`,
        { withAuth: true }
      );
      if (sequence !== requestSequenceRef.current) return;
      setViewingDoc(doc);
      setDocProblemPage(response.data);
      if (page === 1) setCheckedProblemIdList([]);
      // 좁은 화면에서는 문항이 폴더 목록 아래에 열린다. 열린 자리로 데려간다
      if (window.innerWidth <= 860) {
        requestAnimationFrame(() => problemPaneRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }));
      }
    } catch (error) {
      if (sequence !== requestSequenceRef.current) return;
      alert(error?.response?.data?.error || "문서 문항을 불러오지 못했습니다.");
    } finally {
      if (sequence === requestSequenceRef.current) setIsLoadingProblems(false);
    }
  };

  // 조건을 바꾸면 열어 둔 문서를 그 조건으로 다시 받는다
  const viewingDocRef = useRef(null);
  viewingDocRef.current = viewingDoc;
  useEffect(() => {
    if (viewingDocRef.current) loadDocProblems(viewingDocRef.current, 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterQuery]);

  const addCheckedProblems = () => {
    const checkedList = docProblemPage.problem_list.filter((one) => checkedProblemIdList.includes(one.problem_id));
    if (!checkedList.length) return alert("담을 문제를 체크해주세요.");
    if (onAddProblems(checkedList, viewingDoc?.title || "")) setCheckedProblemIdList([]);
  };

  const pickFromDocs = async () => {
    if (!docIdList.length) return alert("문서를 골라주세요.");
    if (isPicking) return;
    setIsPicking(true);
    try {
      const response = await apiClient.post(
        "/api/v1/assignment/math/bank/pick",
        { docs: docIdList, total, ...problemFilterPayloadOf(filter) },
        { withAuth: true }
      );
      if (!response.data.problem_list.length) return alert("조건에 맞는 문제가 없습니다.");
      onPicked(response.data.problem_list, docTitleById[docIdList[0]] || "");
    } catch (error) {
      alert(error?.response?.data?.error || "문제를 뽑지 못했습니다.");
    } finally {
      setIsPicking(false);
    }
  };

  const renderFolders = () => (
    <section className="math-bank-page__card math-bank-page__card--folder">
      <div className="math-bank-page__card-head">
        <h4>내 PDF</h4>
      </div>
      {!docData && <p className="math-bank-page__empty">불러오는 중입니다.</p>}
      {docData?.error && <p className="math-bank-page__empty">{docData.error}</p>}
      {docData && !docData.error && !docSectionList.length && <p className="math-bank-page__empty">올린 문서가 없어요.</p>}
      {docSectionList.length > 1 && (
        <MathBankChipList
          optionList={docSectionList.map((one) => ({ value: one.section, label: one.name }))}
          isActive={(value) => value === docSectionKey}
          onPick={setDocSectionKey}
        />
      )}
      <div className="math-bank-page__folder-list">
        {(docSection?.folders || []).map((folder) => {
          const folderKey = `${docSectionKey}:${folder.id ?? "none"}`;
          const isOpen = openFolderKeyList.includes(folderKey) || docSection.folders.length === 1;
          return (
            <div key={folderKey} className="math-bank-page__folder">
              <button
                type="button"
                className="math-bank-page__folder-head"
                aria-expanded={isOpen}
                onClick={() => setOpenFolderKeyList((list) => toggleInList(list, folderKey))}
              >
                <span className={`math-bank-page__chevron-mark${isOpen ? " math-bank-page__chevron-mark--open" : ""}`} />
                <span className="math-bank-page__folder-name">{folder.name}</span>
                <span className="math-bank-page__folder-count">{folder.docs.length}</span>
              </button>
              {isOpen &&
                folder.docs.map((doc) => (
                  <div
                    key={doc.id}
                    className={`math-bank-page__doc${viewingDoc?.id === doc.id ? " math-bank-page__doc--viewing" : ""}`}
                  >
                    <input
                      type="checkbox"
                      className="math-bank-page__doc-check"
                      aria-label="조건으로 뽑을 문서"
                      checked={docIdList.includes(doc.id)}
                      onChange={() => setDocIdList((list) => toggleInList(list, doc.id))}
                    />
                    <button type="button" className="math-bank-page__doc-title" onClick={() => loadDocProblems(doc)}>
                      {doc.title}
                    </button>
                  </div>
                ))}
            </div>
          );
        })}
      </div>
      {docIdList.length > 0 && (
        <div className="math-bank-page__doc-pick">
          <div className="math-bank-page__doc-pick-title">고른 문서 {docIdList.length}개에서 뽑기</div>
          <div className="math-bank-page__doc-pick-row">
            <input
              type="number"
              className="math-bank-page__number-input"
              min={1}
              max={MAX_TOTAL}
              value={total}
              aria-label="문항 수"
              onChange={(event) => setTotal(Math.max(1, Math.min(MAX_TOTAL, Number(event.target.value) || 1)))}
            />
            <span className="math-bank-page__unit-text">문제</span>
            <button type="button" className="math-bank-page__primary" onClick={pickFromDocs} disabled={isPicking}>
              {isPicking ? "뽑는 중" : "뽑기"}
            </button>
            <button type="button" className="math-bank-page__text-button" onClick={() => setDocIdList([])}>
              풀기
            </button>
          </div>
        </div>
      )}
    </section>
  );

  const renderProblems = () => {
    const pageProblemIdList = docProblemPage.problem_list.map((one) => one.problem_id);
    const isAllChecked = pageProblemIdList.length > 0 && pageProblemIdList.every((id) => checkedProblemIdList.includes(id));
    return (
      <section ref={problemPaneRef} className="math-bank-page__card">
        <div className="math-bank-page__card-head">
          <h4 className="math-bank-page__doc-heading">{viewingDoc ? viewingDoc.title : "문항"}</h4>
          {viewingDoc && (
            <div className="math-bank-page__head-actions">
              <button
                type="button"
                className="math-bank-page__secondary"
                onClick={() => setCheckedProblemIdList(isAllChecked ? [] : pageProblemIdList)}
              >
                {isAllChecked ? "모두 풀기" : "모두 체크"}
              </button>
              <button type="button" className="math-bank-page__primary" onClick={addCheckedProblems}>
                {checkedProblemIdList.length ? `${checkedProblemIdList.length}문제 담기` : "체크한 문제 담기"}
              </button>
            </div>
          )}
        </div>
        <div className="math-bank-page__filter-bar">
          <MathBankFilterFields filter={filter} onChange={onFilterChange} />
        </div>
        {!viewingDoc && !isLoadingProblems && <p className="math-bank-page__empty">문서를 골라 주세요.</p>}
        {isLoadingProblems && <p className="math-bank-page__empty">불러오는 중입니다.</p>}
        {viewingDoc && !isLoadingProblems && !docProblemPage.problem_list.length && (
          <p className="math-bank-page__empty">조건에 맞는 문제가 없습니다.</p>
        )}
        {viewingDoc && !isLoadingProblems && docProblemPage.problem_list.length > 0 && (
          <ol className="math-bank-page__problem-list">
            {docProblemPage.problem_list.map((problem, index) => {
              const isPicked = pickedProblemIdList.includes(problem.problem_id);
              const isChecked = checkedProblemIdList.includes(problem.problem_id);
              return (
                <MathBankProblemCard
                  key={problem.problem_id}
                  problem={problem}
                  index={(docProblemPage.page - 1) * 100 + index}
                  isChecked={isChecked}
                  checkbox={
                    <input
                      type="checkbox"
                      className="math-bank-page__problem-check"
                      aria-label="담을 문제"
                      checked={isChecked}
                      onChange={() => setCheckedProblemIdList((list) => toggleInList(list, problem.problem_id))}
                    />
                  }
                  actionList={isPicked ? <span className="math-bank-page__picked-mark">담음</span> : null}
                />
              );
            })}
          </ol>
        )}
        {viewingDoc && docProblemPage.pages > 1 && (
          <div className="math-bank-page__pager">
            {Array.from({ length: docProblemPage.pages }, (_, index) => index + 1).map((page) => (
              <button
                key={page}
                type="button"
                className={`math-bank-page__chip${page === docProblemPage.page ? " math-bank-page__chip--active" : ""}`}
                onClick={() => loadDocProblems(viewingDoc, page)}
              >
                {page}
              </button>
            ))}
          </div>
        )}
      </section>
    );
  };

  return (
    <div className="math-bank-page__layout math-bank-page__layout--doc">
      {renderFolders()}
      {renderProblems()}
    </div>
  );
};

export default MathBankDocPanel;
